/**
 * The Add to mailshot box's tick-box for homes flagged as a development: its words, worked out
 * apart from the box so that they can be tested (EST-192, Oct 2026).
 *
 * The rule itself is models/developmentFlag.js. This is what the box SAYS of it:
 *  - how many of the homes that would be added are flagged, and whether they are left out;
 *  - unticked, the consequence in words: N homes flagged as a development WILL be added;
 *  - how many have NO FLAG RECORDED, in words, whenever there are any. They cannot be told,
 *    and they are added: the box must never read as "none of these is a development". An
 *    import's homes have no flag by nature (its file carries none), and are said apart, so
 *    that "no flag recorded: 1,102" on an import's page does not read as a fault;
 *  - and, where the box was given ids and not records, that the homes were NOT READ for the
 *    flag at all, and how many.
 *
 * ⚠️ ONE SET. The number on the tick-box, the "Show them" list and the "left out" figure in
 * the message after the add all come from `flaggedAmong` of the same homes. A test holds
 * the three to each other.
 */
import { FLAGGED, NO_FLAG, developmentFlagOf, flaggedBy } from '../models/developmentFlag';
import { sourceOf, IMPORT, SCRAPE } from '../models/propertySource';

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const homes = (count) => (Number(count) === 1 ? 'home' : 'homes');
const list = (v) => (Array.isArray(v) ? v : []);

const hasId = (p) => p && p.id !== undefined && p.id !== null && String(p.id) !== '';
/**
 * A selection with each HOME once: a record given twice (the same id) is one home, and is counted, listed and said once.
 * The first of them is kept, in the order given. A record with no id cannot be told from another, so each is kept.
 */
export function onePerHome(properties) {
  const seen = new Set();
  return list(properties).filter((p) => { if (!hasId(p)) return true; const id = String(p.id); if (seen.has(id)) return false; seen.add(id); return true; });
}

/** The homes of a selection that are flagged as a development, each once, in the order given. */
export const flaggedAmong = (properties) => onePerHome(properties).filter((p) => developmentFlagOf(p) === FLAGGED);

/** A selection without its flagged homes. Homes with no flag recorded stay: they cannot be told. */
export const withoutFlagged = (properties) => list(properties).filter((p) => developmentFlagOf(p) !== FLAGGED);

/** Said where the box was given ids only: it could not read the flag. */
export const notReadWords = (count) => `${Number(count) === 1 ? 'This home was' : `These ${n(count)} homes were`} given to this box by id only, so ${Number(count) === 1 ? 'it was' : 'they were'} NOT read for the development flag: a home flagged as a development would be added with the rest. A home's own page shows the flag; adding from a scrape reads it.`;

/**
 * The tick-box and the lines under it.
 * @param {Object[]} properties the homes that would otherwise be added (after the status, mailing-rule and price choices)
 * @param {boolean}  leaveOut   the tick-box
 * @returns {{ flagged, noFlag, noFlagImported, noFlagNeverScraped, label, hint, untickedWords, noFlagWords, importedWords, neverScrapedWords }}
 *   The counts are of HOMES (a record given twice is one). `noFlag` is the scraped homes saved before the flags were
 *   written; an import's and the never-scraped (added by hand, or a HubSpot quote) are counted and said apart.
 */
export function developmentChoice(properties, leaveOut = true) {
  const all = onePerHome(properties);
  const flagged = flaggedAmong(all).length;
  const unknown = all.filter((p) => developmentFlagOf(p) === NO_FLAG);
  const noFlagImported = unknown.filter((p) => sourceOf(p).kind === IMPORT).length;
  const noFlag = unknown.filter((p) => sourceOf(p).kind === SCRAPE).length;
  const noFlagNeverScraped = unknown.length - noFlagImported - noFlag;
  // the homes that COULD say, and said no
  const saidNo = all.length - flagged - unknown.length;
  const is = (count) => (count === 1 ? 'is' : 'are');
  return {
    flagged, noFlag, noFlagImported, noFlagNeverScraped,
    label: `Leave out ${n(flagged)} ${homes(flagged)} flagged as a development`,
    // with nothing flagged: a claim is made only of homes that have a flag. When NONE has, say that, not "none is flagged".
    hint: flagged
      ? '(On by default: new homes are not what we mail.)'
      : saidNo
        ? '(On by default. No home here that has a flag recorded is flagged.)'
        : (all.length ? '(On by default. None of these homes has a flag recorded.)' : '(On by default.)'),
    // unticking is a visible act, with its consequence in words
    untickedWords: !leaveOut && flagged ? `${n(flagged)} ${homes(flagged)} flagged as a development WILL be added.` : null,
    noFlagWords: noFlag
      ? `No flag recorded: ${n(noFlag)}. ${noFlag === 1 ? 'That home was' : 'Those homes were'} saved before the scrapers wrote the flag, so ${noFlag === 1 ? 'it' : 'they'} cannot be told, and ${is(noFlag)} added.`
      : null,
    importedWords: noFlagImported
      ? `${n(noFlagImported)} ${noFlagImported === 1 ? 'was' : 'were'} brought in by an import. An import's file has no development flag, so there is nothing to read: this box cannot tell which of them is a new home, and ${noFlagImported === 1 ? 'it is' : 'they are'} added.`
      : null,
    // a home added by hand, or from a HubSpot quote, was never scraped: "saved before the scrapers wrote the flag" would be untrue of it
    neverScrapedWords: noFlagNeverScraped
      ? `${n(noFlagNeverScraped)} ${noFlagNeverScraped === 1 ? 'was' : 'were'} added by hand or from a HubSpot quote and never scraped, so there is no development flag to read: ${noFlagNeverScraped === 1 ? 'it is' : 'they are'} added.`
      : null,
  };
}

/**
 * What the message after an add says of it. Empty when there is nothing to say.
 * @param {Object} given `hadRecords` (the box was given records, not ids), `leaveOut` (the tick-box),
 *   `flagged`, `noFlag`, `noFlagImported`, `noFlagNeverScraped` as developmentChoice counts them, and
 *   `flaggedSent`: how many of the flagged homes were among those SENT to be added (after an unticked add the check
 *   may still have left some out, as a double or for do not contact: "0 of 2" must not read as "2 of 2").
 */
export function developmentNote({ hadRecords, leaveOut, flagged, flaggedSent, noFlag, noFlagImported, noFlagNeverScraped }) {
  if (!hadRecords) return 'NOT read for the development flag (the homes were given by id only)';
  const parts = [];
  if (flagged && leaveOut) parts.push(`${n(flagged)} flagged as a development left out`);
  if (flagged && !leaveOut) {
    const sent = Number(flaggedSent || 0);
    parts.push(`the box for homes flagged as a development was UNTICKED: ${n(sent)} of the ${n(flagged)} such ${homes(flagged)} ${sent === 1 ? 'was' : 'were'} among those sent to be added`);
  }
  const unknown = Number(noFlag || 0) + Number(noFlagImported || 0) + Number(noFlagNeverScraped || 0);
  if (unknown) parts.push(`${n(unknown)} with no development flag recorded could not be told and ${unknown === 1 ? 'was' : 'were'} not held back`);
  return parts.join('; ');
}

/** One flagged home for the "Show them" list: its address in a line, which flag says so, and where its own page is. */
export function flaggedRow(property) {
  const p = property || {};
  const address = [p.address || p.fullAddress || p.displayAddress, p.postcode]
    .filter((v) => typeof v === 'string' && v.trim())
    .map((v) => v.trim())
    .filter((v, i, all) => i === 0 || !all[0].toUpperCase().includes(v.toUpperCase()))
    .join(', ');
  // a record with no id has no page of its own to open: no link is made for it (it used to be ".../property/undefined")
  const id = hasId(p) ? String(p.id) : null;
  return { id, address: address || 'no address on the record', by: flaggedBy(p), href: id === null ? null : `/dashboard/properties/property/${encodeURIComponent(id)}` };
}
